import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { useGetBillingEntity } from 'src/features/billingEntity/api/useGetBillingEntity';
import { billingEntityFormSchema, BillingEntityFormSchemaValues } from 'src/features/billingEntity/utils/formSchema';
import BillingEntityForm from 'src/features/billingEntity/components/forms/BillingEntityForm';
import { BillingEntity } from 'src/features/billingEntity/utils/types';
import { useParking } from 'src/providers/ParkingProvider';

interface BillingEntityFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillingEntity?: BillingEntity;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: BillingEntityFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function BillingEntityFormModal<TData>({
    selectedBillingEntity,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: BillingEntityFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    // We are in edit mode if we have a selected service provider
    // otherwise it means we are creating a new service provider

    const { data: billingEntity } = useGetBillingEntity({
        entityId: selectedBillingEntity?.id ?? '',
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedBillingEntity?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            billingEntity || {
                name: '',
                email: '',
                uic: '',
                code: '',
                tax_registration_number: '',
            },
        [billingEntity],
    );

    const form = useForm<BillingEntityFormSchemaValues>({
        resolver: zodResolver(billingEntityFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [billingEntity, isModalOpen]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: BillingEntityFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <BillingEntityForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedBillingEntity && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BillingEntityFormModal;
